import { Box, ToastType, useModalState } from '@bechtel/gds';
import { MetadataTableName, Question, UiMessageKey, UiTextEnum } from '@bechtel/sr-types';
import { forEach, includes } from 'lodash';
import { FC, lazy, useCallback, useMemo, useRef } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { CloneAction, CreateAction, DeleteAction, FormMode, QuestionModalResponse, useLanguage } from '~/components';
import { useDeleteCommodity, useToast } from '~/hooks';
import { useGetCommodityByIdQuery } from '~/hooks/use-get-commodity-by-id';
import CommodityMenu from '../commodity-header/menu';
import { CommodityActionsProps } from './types';
import { getCommodityIdentifier, getDeleteErrors, getDeleteInput, getEntityText } from './utils';
const MessageModal = lazy(() => import('~/components/organisms/message-modal/message'));

export const CommodityActions: FC<CommodityActionsProps> = ({
  canAddItem,
  canDeleteItem,
  selectedRowId,
  commodityName,
  tag,
  onDelete,
  enableOptions = true,
  formMode,
  tableMemberName,
}) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { getMultiLingualTextByKey } = useLanguage();
  // const [userPreferences] = useLocalStorage('User Preferences', true); // workAround, will remove this part when the UI Screen is ready
  const userPreferences = false;
  const [deleteCommodity, { data }] = useDeleteCommodity(commodityName);
  const [getCommodityById] = useGetCommodityByIdQuery(commodityName, true);
  const entityText = getMultiLingualTextByKey(getEntityText(commodityName));
  const enableClone = (canAddItem && selectedRowId) || (canAddItem && formMode === FormMode.UPDATE);
  const enableDelete = (canDeleteItem && selectedRowId) || (canDeleteItem && formMode === FormMode.UPDATE);
  const params = useParams();
  const commodityId = selectedRowId || params?.id || '';
  const deleteMutationInput = getDeleteInput(commodityName, commodityId);
  const modalResponse = useRef<Question>(Question.NOT_ASKED);
  const { visible, open, close } = useModalState(false);

  const deleteHandler = useCallback(
    async (deleteInput) => {
      const handleDelete = async () => {
        await deleteCommodity({
          variables: {
            ...deleteInput,
          },
          update(cache, { data }) {
            if (!data) return;
            const errors = getDeleteErrors(commodityName, data);
            if (errors && errors.length > 0) return;
            const identifier = getCommodityIdentifier(commodityName);
            cache.evict({ id: `${identifier}:${commodityId}` });
            if (tableMemberName) {
              // tableMemberName is passed from the grid.
              // So if it is a grid, we will evict the Count cache, so that updated count is reflected in the status-bar
              cache.evict({
                id: 'ROOT_QUERY',
                fieldName: `${tableMemberName}Count`,
              });
            }
            cache.gc();
          },
          onCompleted: (data) => {
            const errors = getDeleteErrors(commodityName, data);
            if (errors && errors.length > 0) {
              if (includes(errors, UiMessageKey.COMPONENT_STATUSED_AS_DELETED)) {
                const deletedItem = `delete${getCommodityIdentifier(commodityName)}`;
                getCommodityById({
                  variables: {
                    id: data[deletedItem]?.id,
                  },
                  onCompleted: (data) => {
                    const commodity = `${commodityName?.toLowerCase()}ById`;
                    if (onDelete) {
                      onDelete(data[commodity]?.id, data[commodity]);
                    }
                  },
                });
              }
              return;
            }
            if (onDelete) onDelete(commodityId ? commodityId : '');
            showToast(UiTextEnum.DELETED_SUCCESSFULLY_TOAST, [entityText, tag ?? ''], ToastType.INFO);
            if (formMode) setTimeout(() => navigate('..', { replace: true }));
          },
        });
      };
      close();
      if (
        userPreferences &&
        (modalResponse.current === Question.NOT_ASKED || modalResponse.current === Question.REPLY_NO)
      ) {
        return;
      } else {
        await handleDelete();
      }
    },
    [
      close,
      userPreferences,
      deleteCommodity,
      commodityName,
      commodityId,
      tableMemberName,
      onDelete,
      showToast,
      entityText,
      tag,
      formMode,
      getCommodityById,
      navigate,
    ]
  );

  const errorKeys = useMemo(() => {
    if (!data) return;
    const deleteErrors = getDeleteErrors(commodityName, data);
    if (!deleteErrors || deleteErrors.length === 0) return;
    return deleteErrors;
  }, [data, commodityName]);

  const handleQuestions = async (questionResponses: QuestionModalResponse[]) => {
    if (!commodityId) return;
    const questionValues: {
      deleteAllTerms?: Question;
      deleteFromTerms?: Question;
      deleteToTerms?: Question;
      deleteRoute?: Question;
      isPhysicallyRemoved?: Question;
    } = {};
    forEach(questionResponses, (questionResponse) => {
      if (questionResponse.messageKey == UiMessageKey.CABLE_CODE_CONFIGURATION_REVISION_FLAG_NOT_SET) {
        questionValues.deleteAllTerms = questionResponse.response;
      }
      if (questionResponse.messageKey == UiMessageKey.FROM_END_DETERMINATION_FLAG_NOT_SET) {
        questionValues.deleteFromTerms = questionResponse.response;
      }
      if (questionResponse.messageKey == UiMessageKey.PHYSICAL_REMOVAL_FLAG_NOT_SET) {
        questionValues.deleteRoute = questionResponse.response;
      }
      if (questionResponse.messageKey == UiMessageKey.TO_END_DETERMINATION_FLAG_NOT_SET) {
        questionValues.deleteToTerms = questionResponse.response;
      }
      if (questionResponse.messageKey == UiMessageKey.RACEWAY_PHYSICAL_REMOVAL_FLAG_NOT_SET) {
        questionValues.isPhysicallyRemoved = questionResponse.response;
      }
      if (questionResponse.messageKey == UiMessageKey.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_ITEM) {
        modalResponse.current = questionResponse.response;
      }
    });
    let deleteInput;
    if (commodityName === MetadataTableName.RACEWAY) {
      deleteInput = {
        deleteRacewayInput: {
          id: commodityId,
          isPhysicallyRemoved: questionValues.isPhysicallyRemoved,
        },
      };
    } else if (commodityName === MetadataTableName.CABLE) {
      deleteInput = {
        deleteCableInput: {
          id: commodityId,
          deleteAllTerms: questionValues.deleteAllTerms,
          deleteFromTerms: questionValues.deleteFromTerms,
          deleteRoute: questionValues.deleteRoute,
          deleteToTerms: questionValues.deleteToTerms,
        },
      };
    } else {
      deleteInput = getDeleteInput(commodityName, commodityId);
    }
    await deleteHandler(deleteInput);
  };

  const userPreferenceMessageKeys =
    (userPreferences && modalResponse.current !== Question.REPLY_NO) || !userPreferences ? errorKeys : [];

  return (
    <>
      {enableOptions && (
        <>
          <Box mr={{ sm: '200' }}>
            <NavLink
              to={{ pathname: !formMode ? 'new' : '../new/details' }}
              onClick={(event) => !canAddItem && event.preventDefault()}
              tabIndex={-1}
            >
              <CreateAction canAddItem={Boolean(canAddItem)} />
            </NavLink>
          </Box>
          <Box mr={{ sm: '200' }}>
            <NavLink
              to={{ pathname: !formMode ? 'new/details' : '../new/details' }}
              state={{ mode: FormMode.CLONE, id: commodityId }}
              onClick={(event) => !enableClone && event.preventDefault()}
              tabIndex={-1}
              style={{ cursor: !enableClone ? 'initial' : 'pointer' }}
            >
              <CloneAction enableClone={Boolean(enableClone)} />
            </NavLink>
          </Box>
          <Box mr={{ sm: '200' }}>
            <DeleteAction
              enableDelete={Boolean(enableDelete)}
              deleteHandler={() => (userPreferences ? open() : deleteHandler(deleteMutationInput))}
            />
          </Box>
        </>
      )}
      <Box pr={{ sm: !formMode ? '100' : '400' }}>
        <CommodityMenu />
      </Box>
      {(visible || (errorKeys && errorKeys.length > 0)) && (
        <MessageModal
          onSubmit={handleQuestions}
          messageKeys={
            (visible && userPreferences
              ? [UiMessageKey.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_ITEM]
              : userPreferenceMessageKeys) ?? []
          }
        ></MessageModal>
      )}
    </>
  );
};
